// src/utils/sourceFreshness.js
//
// Pass 82D §12/§13 — source freshness derived from the EXISTING sync metadata (`syncRuns`: a latest-attempt doc + an
// authoritative last-success doc), reusing the accepted cadence-aware staleness threshold. A schedule instructors rely
// on must say how fresh it is, and a FAILED read is NEVER treated as "0 bookings" (fail-closed remains mandatory — this
// module only DESCRIBES freshness; it never mutates canonical data). Distinguishes healthy / syncing / stale / failed /
// not_configured (§13). PURE (no Firestore, no React).

// Automated cadence 07:00/12:00/19:00 JST → max healthy gap 12h + 1h grace (mirrors SyncNowPanel.CADENCE_STALE_MS).
export const CADENCE_STALE_MS = 13 * 60 * 60 * 1000;

export const SYNC_STATUS = {
  HEALTHY: 'healthy',
  SYNCING: 'syncing',
  STALE: 'stale',
  FAILED: 'failed',
  NOT_CONFIGURED: 'not_configured',
};

export function tsMs(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : (ts?._seconds ? new Date(ts._seconds * 1000) : (ts instanceof Date ? ts : null)));
    return d ? d.getTime() : null;
  } catch (e) { return null; }
}

// Derive the freshness of ONE source lane. Inputs are the same docs SyncNowPanel reads (latest attempt + last success).
// `configured=false` → the source is not set up (never falsely "healthy" or "0"). `nowMs` is passed in (pure).
export function deriveSyncFreshness({ latestAttempt = null, lastSuccess = null, nowMs = 0, inFlight = false, configured = true } = {}) {
  if (!configured) return { status: SYNC_STATUS.NOT_CONFIGURED, staleMs: null, lastSuccessMs: null };
  const lastSuccessMs = tsMs(lastSuccess && lastSuccess.finishedAt);
  const attemptStatus = String((latestAttempt && latestAttempt.status) || '').toLowerCase();
  const staleMs = lastSuccessMs != null ? Math.max(0, nowMs - lastSuccessMs) : Infinity;
  let status;
  if (inFlight) status = SYNC_STATUS.SYNCING;
  else if (attemptStatus === 'error' || attemptStatus === 'failed') status = SYNC_STATUS.FAILED;
  else if (lastSuccessMs == null) status = SYNC_STATUS.FAILED; // never a successful read → needs attention, NOT healthy
  else if (staleMs > CADENCE_STALE_MS) status = SYNC_STATUS.STALE;
  else status = SYNC_STATUS.HEALTHY;
  return { status, staleMs, lastSuccessMs };
}

// Is this freshness state one that operations must be warned about? (drives a Needs-Attention signal, §13/§17).
export function isSourceUnhealthy(status) {
  return status === SYNC_STATUS.STALE || status === SYNC_STATUS.FAILED;
}

// Human label for a freshness status (never raw programmer jargon in primary UI, §44).
export function freshnessLabel(status) {
  return {
    [SYNC_STATUS.HEALTHY]: 'Up to date',
    [SYNC_STATUS.SYNCING]: 'Syncing…',
    [SYNC_STATUS.STALE]: 'Schedule may be out of date',
    [SYNC_STATUS.FAILED]: 'Sync problem — schedule not refreshed',
    [SYNC_STATUS.NOT_CONFIGURED]: 'Not connected',
  }[status] || '';
}
